import clsx from "clsx";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { Accordion as BootstrapAccordion, Card } from "react-bootstrap";
import styles from "./Accordion.scss";

const propTypes = {
  /**
   * Tabs of the Accordion
   */
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.node.isRequired,
      content: PropTypes.node.isRequired,
    })
  ).isRequired,
  /**
   * Accordion style
   */
  appearance: PropTypes.oneOf(["block", "clean"]),
  /**
   * If true, display open/close arrow in the Accordion tab header
   */
  showArrow: PropTypes.bool,
  /**
   * Position of the arrow in the header
   */
  arrowPosition: PropTypes.oneOf(["start", "end"]),
  /**
   * If true, accordion will be collapsed first time
   */
  defaultCollapsed: PropTypes.bool,
  /**
   * If true, highlights opened tab header
   */
  highlightOpenedTab: PropTypes.bool,
};

const defaultProps = {
  appearance: "block",
  showArrow: false,
  defaultCollapsed: false,
  arrowPosition: "end",
  highlightOpenedTab: false,
};

/**
 * Accordion component based on https://react-bootstrap-v4.netlify.app/components/accordion/.
 */
function Accordion(props) {
  const {
    tabs,
    appearance,
    showArrow,
    defaultCollapsed,
    arrowPosition,
    highlightOpenedTab,
  } = props;

  // Since the bootstrap Accordion opens does not allow multiple open tabs
  // the workaround is to use multiple accordions to obtain the desired design
  return (
    <div>
      {tabs.map(({ id, label, content }) => (
        <AccordionTab
          key={id}
          id={id}
          label={label}
          content={content}
          appearance={appearance}
          showArrow={showArrow}
          defaultCollapsed={defaultCollapsed}
          arrowPosition={arrowPosition}
          highlightOpenedTab={highlightOpenedTab}
        />
      ))}
    </div>
  );
}

// disabling proptypes on the sub components below
// since they are already documented on the main component
/* eslint-disable react/prop-types */
function AccordionTab(props) {
  const {
    id,
    label,
    content,
    appearance,
    showArrow,
    defaultCollapsed,
    arrowPosition,
    highlightOpenedTab,
  } = props;

  const [selected, setSelected] = useState(defaultCollapsed ? null : id);

  return (
    <BootstrapAccordion
      defaultActiveKey={defaultCollapsed ? null : id}
      className={`${styles.base} ${styles[appearance]}`}
      onSelect={setSelected}
    >
      <Card
        className={clsx(
          highlightOpenedTab && !!selected && styles.highlightHeader
        )}
      >
        <BootstrapAccordion.Toggle
          as={Card.Header}
          eventKey={id}
          className={clsx(
            "d-flex",
            arrowPosition === "end"
              ? "justify-content-between"
              : "justify-content-start"
          )}
        >
          {showArrow && arrowPosition === "start" && (
            <Arrow open={!!selected} position={arrowPosition} />
          )}
          {label}
          {showArrow && arrowPosition === "end" && (
            <Arrow open={!!selected} position={arrowPosition} />
          )}
        </BootstrapAccordion.Toggle>
        <BootstrapAccordion.Collapse eventKey={id}>
          <Card.Body>{content}</Card.Body>
        </BootstrapAccordion.Collapse>
      </Card>
    </BootstrapAccordion>
  );
}

function Arrow(props) {
  const { open, position } = props;
  return (
    <div
      className={clsx(
        "d-flex align-items-center",
        position === "end" ? "justify-content-end" : "justify-content-start",
        styles.controls
      )}
    >
      <i
        className={`${styles.arrow} fas fa-chevron-${open ? "down" : "right"}`}
      />
    </div>
  );
}

Accordion.propTypes = propTypes;
Accordion.defaultProps = defaultProps;

export default Accordion;
